import { useEffect, useRef, type RefObject } from 'react';
import type { MapRef } from '@vis.gl/react-maplibre';
import { useCameraControl } from '../../store/cameraControl';

const _cameraToken = import.meta.env.VITE_CAMERA_TOKEN || '';
const _baseWs = import.meta.env.DEV
  ? 'ws://localhost:8310/ws/camera'
  : `wss://${window.location.host}/camera-ws`;
const WS_URL = _cameraToken ? `${_baseWs}?token=${_cameraToken}` : _baseWs;

const RECONNECT_BASE_MS = 3000;
const RECONNECT_MAX_MS = 30000;

export interface NarrativeMeta {
  text: string;
  alarm?: { driverId: number; code: string };
  narrativeDuration?: number; // ms — overrides default auto-hide
}

interface CameraControllerProps {
  mapRef: RefObject<MapRef | null>;
  ready: boolean;
  onNarrative?: (meta: NarrativeMeta | null) => void;
  onAlarm?: (alarm: { driverId: number; code: string } | null) => void;
}

export function CameraController({ mapRef, ready, onNarrative, onAlarm }: CameraControllerProps) {
  const wsRef = useRef<WebSocket | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();
  const backoffRef = useRef(RECONNECT_BASE_MS);

  useEffect(() => {
    if (!ready) return;

    function connect() {
      const ws = new WebSocket(WS_URL);
      wsRef.current = ws;

      ws.onopen = () => {
        backoffRef.current = RECONNECT_BASE_MS;
      };

      ws.onmessage = (ev) => {
        try {
          const msg = JSON.parse(ev.data);
          // El 'welcome' reenvía el último comando retenido por el servidor.
          // Se ignora: solo se procesan comandos en vivo, para que la cámara
          // no salte a un cruce de una sesión anterior al cargar la app.
          if (msg.type === 'welcome') return;
          const data = msg;
          if (!data) return;

          // En modo manual ('c') la cámara queda al operador: se ignora el
          // movimiento automático (alarmas, demo), pero la narrativa/alarma
          // se siguen emitiendo. Los comandos `source: 'manual'` (tecla I
          // del operador) siempre se aplican — son acciones explícitas.
          const cc = useCameraControl.getState();
          const manualOverride = Date.now() < cc.manualUntil;
          const allowFly = data.source === 'manual' || (cc.autoMove && !manualOverride);
          if (data.type === 'fly_to' && allowFly) {
            const map = mapRef.current?.getMap();
            if (map) {
              const flyOpts: {
                center?: [number, number];
                zoom?: number;
                bearing?: number;
                pitch?: number;
                duration?: number;
              } = { duration: data.duration ?? 2000 };

              if (data.center) flyOpts.center = data.center;
              if (data.zoom != null) flyOpts.zoom = data.zoom;
              if (data.bearing != null) flyOpts.bearing = data.bearing;
              if (data.pitch != null) flyOpts.pitch = data.pitch;

              map.flyTo(flyOpts);
            }
          }

          onNarrative?.(data.narrative
            ? { text: data.narrative, alarm: data.alarm, narrativeDuration: data.narrative_duration ?? undefined }
            : null
          );
          onAlarm?.(data.alarm ?? null);
        } catch {
          // ignore malformed messages
        }
      };

      ws.onclose = () => {
        const delay = backoffRef.current;
        backoffRef.current = Math.min(delay * 2, RECONNECT_MAX_MS);
        timerRef.current = setTimeout(connect, delay);
      };

      ws.onerror = () => ws.close();
    }

    connect();

    return () => {
      clearTimeout(timerRef.current);
      wsRef.current?.close();
    };
  }, [ready, mapRef, onNarrative, onAlarm]);

  return null;
}
